import { useRef, useEffect } from 'react';
import { useAutocompleteSuggestions } from '../hooks/useAutocompleteSuggestions';

/**
 * `SearchAutocomplete` の入力プロパティ。
 *
 * @typedef {Object} SearchAutocompleteProps
 * @property {string} searchQuery 検索入力値
 * @property {string} documentType 文書種別コード
 * @property {'life'|'sonpo'|string} activeTab 現在のタブ
 * @property {boolean} isSearchDisabled 検索機能の無効状態
 * @property {boolean} isLoading 検索中フラグ
 * @property {string} placeholder 入力プレースホルダー
 * @property {(value: string) => void} onQueryChange 入力変更コールバック
 * @property {() => void} onSearch 検索実行コールバック
 */

/**
 * Elasticsearchの候補表示を伴う検索入力UI。
 *
 * @param {SearchAutocompleteProps} props コンポーネント入力
 */
const SearchAutocomplete = ({
  searchQuery,
  documentType,
  activeTab,
  isSearchDisabled,
  isLoading,
  searchExecutionSignal = 0,
  placeholder,
  onQueryChange,
  onSearch,
}) => {
  const autocompleteRef = useRef(null);

  const {
    suggestions,
    isSuggestionLoading,
    isSuggestionOpen,
    activeIndex,
    shouldUseAutocomplete,
    setActiveIndex,
    closeSuggestions,
    handleSuggestionSelect,
    handleInputChange,
    handleCompositionStart,
    handleCompositionEnd,
    handleInputFocus,
    handleMiniSearchClick,
    handleKeyDown,
  } = useAutocompleteSuggestions({
    searchQuery,
    documentType,
    activeTab,
    isSearchDisabled,
    onQueryChange,
    onSearch,
    isLoading,
    closeSignal: searchExecutionSignal,
  });

  // サジェスト領域外クリック時に候補一覧を閉じる。
  useEffect(() => {
    const handleDocumentClick = (event) => {
      if (autocompleteRef.current && !autocompleteRef.current.contains(event.target)) {
        closeSuggestions();
      }
    };

    document.addEventListener('click', handleDocumentClick);
    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, [closeSuggestions]);

  return (
    <div className="search-autocomplete" ref={autocompleteRef}>
      <div className="search-container-solicitor mt5">
        <input
          type="text"
          id="search-input"
          value={searchQuery}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleInputFocus}
          onCompositionStart={handleCompositionStart}
          onCompositionEnd={handleCompositionEnd}
          placeholder={placeholder}
          className="search-input"
          autoComplete="off"
          disabled={isSearchDisabled}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={isSuggestionOpen}
          aria-controls="search-suggestion-list"
          aria-activedescendant={
            isSuggestionOpen && activeIndex >= 0
              ? `suggestion-item-${activeIndex}`
              : undefined
          }
        />
        <button className="search-button-solicitor-mini" onClick={handleMiniSearchClick}>
          <svg width="17" height="18" viewBox="0 0 17 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M7.98828 9.84033L10.0072 8.0235L16.0914 14.7844C16.5931 15.3419 16.5478 16.2005 15.9903 16.7022Lnan nanLnan nanL15.9903 16.7022C15.4328 17.2039 14.5742 17.1587 14.0725 16.6012L7.98828 9.84033Z" fill="#3DA5E8" />
            <path d="M14.0468 7.11545C14.0468 10.9643 10.9267 14.0844 7.07785 14.0844C3.229 14.0844 0.108887 10.9643 0.108887 7.11545C0.108887 3.2666 3.229 0.146484 7.07785 0.146484C10.9267 0.146484 14.0468 3.2666 14.0468 7.11545Z" fill="white" />
            <path fillRule="evenodd" clipRule="evenodd" d="M7.07785 11.0844C9.26985 11.0844 11.0468 9.30745 11.0468 7.11545C11.0468 4.92345 9.26985 3.14648 7.07785 3.14648C4.88585 3.14648 3.10889 4.92345 3.10889 7.11545C3.10889 9.30745 4.88585 11.0844 7.07785 11.0844ZM7.07785 14.0844C10.9267 14.0844 14.0468 10.9643 14.0468 7.11545C14.0468 3.2666 10.9267 0.146484 7.07785 0.146484C3.229 0.146484 0.108887 3.2666 0.108887 7.11545C0.108887 10.9643 3.229 14.0844 7.07785 14.0844Z" fill="#0073BC" />
          </svg>
        </button>
      </div>

      {isSuggestionOpen && suggestions.length > 0 && (
        <ul
          id="search-suggestion-list"
          className="search-suggestion-list"
          role="listbox"
          aria-label="キーワード取得"
        >
          {suggestions.map((suggestion, index) => (
            <li
              key={`${suggestion}-${index}`}
              id={`suggestion-item-${index}`}
              role="option"
              aria-selected={activeIndex === index}
            >
              <button
                type="button"
                className={`search-suggestion-item ${activeIndex === index ? 'active' : ''}`}
                onMouseEnter={() => setActiveIndex(index)}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => handleSuggestionSelect(suggestion)}
                tabIndex={-1}
              >
                {suggestion}
              </button>
            </li>
          ))}
        </ul>
      )}

      {shouldUseAutocomplete && isSuggestionLoading && (
        <div className="search-suggestion-loading">キーワード取得中...</div>
      )}
    </div>
  );
};

export default SearchAutocomplete;
